import { FAMILY_LABELS, PRODUCT_FAMILIES } from "../../catalog/taxonomy";
import type { ProductFamily } from "../../catalog/taxonomy";

/**
 * The permanent family navigation over product management.
 *
 * ONE family at a time is the management contract, so this is the only control
 * that selects one — the toolbar's "All families" dropdown was removed when
 * these arrived rather than left beside them, because two controls writing the
 * same state is how they come to disagree.
 *
 * All three are always present, including a family with nothing in it. A tab
 * that disappeared when its count reached zero would leave an administrator
 * with no way to reach the empty section they are trying to fill, and would
 * make the shop's shape look like it changes on its own.
 *
 * The markup is the tab pattern this dashboard already uses on the product
 * workspace — a nav of buttons carrying `aria-current`, styled from the same
 * tokens — so it reads as part of the tool rather than as a new idea.
 */
export function ProductFamilyTabs({
  family,
  counts,
  onSelect,
}: {
  family: ProductFamily;
  /** Per family, or null while its count is still unknown. A tab shows no
   *  number rather than a zero it has not earned. */
  counts: Record<ProductFamily, number | null>;
  onSelect: (next: ProductFamily) => void;
}) {
  return (
    <nav className="productList__familyTabs" aria-label="Product family">
      {PRODUCT_FAMILIES.map((key) => {
        const count = counts[key];
        return (
          <button
            key={key}
            type="button"
            className={
              key === family
                ? "productList__familyTab is-active"
                : "productList__familyTab"
            }
            aria-current={key === family ? "page" : undefined}
            data-testid={`family-tab-${key}`}
            onClick={() => onSelect(key)}
          >
            {FAMILY_LABELS[key]}
            {count === null ? null : (
              <span className="productList__familyTabCount num">({count})</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
